import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  alpha
} from '@mui/material';
import { Visibility, VisibilityOff, Email, WhatsApp, Lock, Security } from '@mui/icons-material';

const LoginComptable = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:900px)');

  // Palette inspirée du logo (bleu nuit / teal / vert / gold) - Version claire
  const palette = useMemo(
    () => ({
      navy: '#0B1E3C',
      navy2: '#102B55',
      teal: '#18B6B0',
      teal2: '#39D0C9',
      tealLight: '#E0F7F6',
      green: '#7BC043',
      gold: '#F3C15C',
      paper: '#FFFFFF',
      gradient: 'linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)',
      shadow: '0 10px 40px rgba(11, 30, 60, 0.08)',
      textPrimary: '#1E293B',
      textSecondary: '#64748B'
    }),
    []
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password, false);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: palette.gradient,
        color: palette.textPrimary,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Arrière-plan décoratif léger */}
      <Box
  aria-hidden
  sx={{
    position: 'absolute',
    inset: 0,
    background: `
      radial-gradient(900px 600px at 12% 20%, white, transparent 90%),
      radial-gradient(900px 600px at 85% 25%, white, transparent 90%),
      radial-gradient(900px 600px at 65% 85%, white, transparent 90%),
      linear-gradient(
        180deg,
        rgba(20,45,80,0.1) 0%,
        rgba(20,45,80,0.85) 100%
      )
    `
  }}
/>

      {/* Header avec logo */}
      <Box sx={{ position: 'relative', zIndex: 2, py: 3, px: 2 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: 3,
                overflow: 'hidden',
                border: '2px solid rgba(24, 182, 176, 0.2)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'white'
              }}
            >
              <img
                src="/logo.jpg"
                alt="Tej Flow"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${palette.navy} 0%, ${palette.teal} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  letterSpacing: '-0.5px'
                }}
              >
                Tej Flow
              </Typography>
             Espace Comptable
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Contenu principal */}
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 2,
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          py: { xs: 2, md: 4 }
        }}
      >
        <Grid container spacing={4} alignItems="stretch">
          {/* Formulaire */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 4,
                overflow: 'hidden',
                bgcolor: 'white',
                boxShadow: palette.shadow,
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid rgba(24, 182, 176, 0.1)'
              }}
            >
              {/* En-tête du formulaire */}
              <Box
                sx={{
                  bgcolor: palette.navy,
                  py: 3,
                  px: 4,
                  textAlign: 'center'
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                  Connexion Comptable
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.8)' }}>
                  Accédez à votre tableau de bord sécurisé
                </Typography>
              </Box>

              {/* Corps du formulaire */}
              <Box sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                {error && (
                  <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    onClose={() => setError('')}
                  >
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1 }}>
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="Adresse email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                      variant="outlined"
                      size="medium"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email sx={{ color: palette.teal }} />
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: `0 0 0 3px ${alpha(palette.teal, 0.1)}`
                          }
                        }
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Mot de passe"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      variant="outlined"
                      size="medium"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: palette.teal }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              size="small"
                            >
                              {showPassword ? (
                                <VisibilityOff sx={{ color: palette.navy2 }} />
                              ) : (
                                <Visibility sx={{ color: palette.navy2 }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: `0 0 0 3px ${alpha(palette.teal, 0.1)}`
                          }
                        }
                      }}
                    />

                    <Box sx={{ mt: 'auto' }}>
                      <Button
                        type="submit"
                        fullWidth
                        disabled={loading}
                        size="large"
                        sx={{
                          py: 1.8,
                          borderRadius: 2,
                          fontWeight: 700,
                          fontSize: '1rem',
                          color: 'white',
                          background: `linear-gradient(135deg, ${palette.teal} 0%, rgba(20,45,80,0.85) 100%)`,
                          textTransform: 'none',
                          transition: 'all 0.3s',
                          boxShadow: '0 4px 20px rgba(24, 182, 176, 0.3)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 30px rgba(24, 182, 176, 0.4)',
                            background: `linear-gradient(135deg, ${palette.teal2} 0%, ${palette.green} 100%)`
                          },
                          '&:active': {
                            transform: 'translateY(0)'
                          }
                        }}
                      >
                        {loading ? 'Connexion en cours...' : 'Se connecter'}
                      </Button>

                      <Box sx={{ mt: 3, textAlign: 'center' }}>
                       
                      </Box>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Support */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 4,
                overflow: 'hidden',
                position: 'relative',
                bgcolor: 'white',
                border: `1px solid ${alpha(palette.teal, 0.1)}`,
                boxShadow: palette.shadow,
                display: 'flex',
                flexDirection: 'column',
                background: `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240, 253, 250, 0.9) 100%)`
              }}
            >
              <Box sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: palette.navy, mb: 1 }}>
                  Support & Assistance
                </Typography>
                <Typography variant="body2" sx={{ color: palette.textSecondary, mb: 4 }}>
                    Accédez gratuitement à notre solution !
                    Demandez votre mot de passe dès maintenant par :
                </Typography>

                <Stack spacing={3} sx={{ flex: 1 }}>
                  {/* Email */}
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      bgcolor: palette.tealLight,
                      border: `1px solid ${alpha(palette.teal, 0.2)}`,
                      transition: 'all 0.3s',
                      '&:hover': {
                        bgcolor: alpha(palette.teal, 0.1),
                        borderColor: alpha(palette.teal, 0.4),
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 3,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${alpha(palette.teal, 0.2)} 0%, ${alpha(palette.teal2, 0.2)} 100%)`,
                          border: `1px solid ${alpha(palette.teal, 0.3)}`
                        }}
                      >
                        <Email sx={{ color: palette.teal }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: palette.textSecondary, mb: 0.5 }}>
                          Support Email
                        </Typography>
                        <Typography sx={{ fontWeight: 600, color: palette.textPrimary, fontSize: '1.1rem' }}>
                          contact@compta-flow.com
                        </Typography>
                        <Typography variant="caption" sx={{ color: palette.textSecondary, display: 'block', mt: 1 }}>
                          Réponse garantie sous 24h
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* WhatsApp */}
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      bgcolor: alpha(palette.green, 0.05),
                      border: `1px solid ${alpha(palette.green, 0.2)}`,
                      transition: 'all 0.3s',
                      '&:hover': {
                        bgcolor: alpha(palette.green, 0.1),
                        borderColor: alpha(palette.green, 0.4),
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 3,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${alpha(palette.green, 0.2)} 0%, ${alpha(palette.gold, 0.2)} 100%)`,
                          border: `1px solid ${alpha(palette.green, 0.3)}`
                        }}
                      >
                        <WhatsApp sx={{ color: '#25D366' }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: palette.textSecondary, mb: 0.5 }}>
                          Support WhatsApp
                        </Typography>
                        <Typography sx={{ fontWeight: 600, color: palette.textPrimary, fontSize: '1.1rem' }}>
                           +216 92 996 112
                        </Typography>
                        <Typography variant="caption" sx={{ color: palette.textSecondary, display: 'block', mt: 1 }}>
                          Lundi au vendredi, 9h-18h
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Avertissement */}
                  <Box
                    sx={{
                      mt: 'auto',
                      p: 3,
                      borderRadius: 3,
                      bgcolor: alpha(palette.gold, 0.08),
                      border: `1px solid ${alpha(palette.gold, 0.2)}`
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: palette.navy, fontWeight: 600, mb: 1 }}>
                      ⚡ Problème de connexion ?
                    </Typography>
                    <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                      Contactez-nous immédiatement avec votre adresse email et une capture d'écran de l'erreur pour une résolution rapide.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ 
        position: 'relative', 
        zIndex: 2, 
        py: 3, 
        borderTop: '1px solid rgba(24, 182, 176, 0.1)' 
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            gap: 2 
          }}>
            <Typography variant="caption" sx={{ color: 'white' }}>
              © {new Date().getFullYear()} Tej Flow. Tous droits réservés.
            </Typography>
          
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LoginComptable;